// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CitedClaimsPanel.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CommentCitationLinks.test.tsx
import { ArrowSquareOut } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Preview } from "../../../components/Preview";
import { SpreadsheetView } from "../../../components/SpreadsheetView";
import { useObjectUrl } from "../../../hooks/useObjectUrl";
import { citationAnchorParam } from "../../../routes/settings/routes/knowledge-database/citationAnchor";
import { PdfCitationPreview } from "../../../routes/settings/routes/knowledge-database/components/PdfCitationPreview";
import { KNOWLEDGE_DATABASE_DEFAULT_SEARCH } from "../../../routes/settings/routes/knowledge-database/route";
import { KNOWLEDGE_DATABASE_PATH } from "../../../routes/settings/routes/knowledge-database/search";
import { useSourceFileQuery } from "../../../utils/queries/knowledge";
import {
  type TicketMessageCitation,
  isKnowledgeRuleCitation,
} from "../utils/commentSourceUtils";

/**
 * Shows the cited REGION of the source, not just its name: a PDF page with the
 * bbox/pin, or the cited sheet scrolled to its cell range. Both reuse the
 * renderers the Knowledge Database admin already uses for the same citation
 * (`PdfCitationPreview`, `SpreadsheetView` via `SourceViewerDialog`), so a rule
 * looks the same wherever it is cited from.
 *
 * why the spreadsheet arm matters more than it sounds: a claims registry is a
 * WORKBOOK. Every rule Danone holds is a row in one, so the PDF-only version of
 * this rendered nothing at all for the entire corpus -- the reader got a claim
 * and a file name and no way to check either against the source.
 *
 * Unanchored citations and download failures still fall back silently to the
 * quote-only view above: this is a progressive enhancement, never a blocker.
 * Its own component so `useSourceFileQuery` and `useObjectUrl` stay
 * unconditional hooks.
 */
function KnowledgeRuleCitationPin({
  sourceId,
  pageNumber,
  bbox,
  relX,
  relY,
  cellAnchor,
}: {
  sourceId: string;
  pageNumber?: number;
  bbox?: { x: number; y: number; w: number; h: number };
  relX?: number;
  relY?: number;
  cellAnchor?: {
    sheetIndex?: number;
    sheetName?: string;
    range?: string;
    row?: number;
  };
}) {
  const { data, isLoading } = useSourceFileQuery(sourceId);
  const mimeType = data?.mimeType ?? "";
  const isSpreadsheet =
    mimeType.includes("spreadsheet") ||
    mimeType === "text/csv" ||
    mimeType === "application/vnd.ms-excel";
  // SpreadsheetView fetches by URL, so the cached bytes are handed to it as an
  // in-memory object URL rather than a second presigned download.
  const objectUrl = useObjectUrl(
    isSpreadsheet ? data?.bytes : undefined,
    mimeType,
  );
  // why memoized: a fresh object each render re-runs SpreadsheetView's
  // citation-bounds memo and with it the scroll effect, which yanks the sheet
  // back to the cited cell every time anything re-renders -- so a reader who
  // scrolls away is dragged back while they read. Same defect the sibling
  // viewer already fixed (SourceViewerDialog.tsx). Declared above the early
  // returns below because a hook cannot sit behind one.
  const highlight = useMemo(
    () =>
      cellAnchor
        ? {
            sheetIndex: cellAnchor.sheetIndex,
            sheetName: cellAnchor.sheetName,
            range: cellAnchor.range,
            row: cellAnchor.row,
          }
        : null,
    [
      cellAnchor?.sheetIndex,
      cellAnchor?.sheetName,
      cellAnchor?.range,
      cellAnchor?.row,
    ],
  );
  const { t } = useTranslation("ui");
  if (isLoading) {
    return (
      <Text
        size="1"
        className="text-puntt-neutral-gray-11"
        data-testid="comment-knowledge-rule-citation-pin-loading"
      >
        {t("commentCitationLinks.loadingSource")}
      </Text>
    );
  }
  if (!data) return null;
  if (isSpreadsheet) {
    // A workbook citation without a cell range still opens the sheet: seeing
    // the source beats seeing its file name, and the range is what the
    // highlight needs, not what the view needs.
    if (!objectUrl) return null;
    return (
      // why the height: SpreadsheetView is `flex size-full flex-col` around a
      // `flex-1 overflow-auto` scroller, so in an auto-height wrapper the sheet
      // renders at full length, nothing scrolls, and its scroll-to-the-cited-
      // cell effect has no container to move -- it highlights the right row and
      // never shows it. Matches the sibling viewer (SourceViewerDialog.tsx).
      <div
        data-testid="comment-knowledge-rule-citation-sheet"
        className="h-[70vh] min-h-0"
      >
        <SpreadsheetView url={objectUrl} highlight={highlight} />
      </div>
    );
  }
  // A failed download or a type neither viewer reads: keep the quote-only
  // citation rather than showing a broken viewer.
  if (mimeType !== "application/pdf" || pageNumber == null) return null;
  return (
    <PdfCitationPreview
      data={data.bytes}
      pageNumber={pageNumber}
      bbox={bbox ?? null}
      relX={relX}
      relY={relY}
      testIdPrefix="comment-knowledge-rule-citation"
    />
  );
}

/**
 * Shared Knowledge Database rule citation dialog — statement headline, quote
 * excerpt, and deep link into the Rules tab. Used by comment chips and the
 * Cited Claims rail so both surfaces stay in sync.
 */
export function KnowledgeRuleCitationDialog({
  citation,
  open,
  onOpenChange,
  children,
}: {
  citation: TicketMessageCitation;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional Dialog.Trigger (or other) children. Omit for controlled-only open. */
  children?: ReactNode;
}) {
  const { t } = useTranslation("ui");
  if (!isKnowledgeRuleCitation(citation)) return null;

  const statement = citation.statement?.trim();
  const quote = citation.quote?.trim();
  const sourceName = citation.sourceName?.trim();
  // A page-anchored citation renders the source PDF below the quote. In the
  // default 600px / 420px-tall dialog the page is squeezed into a slit and the
  // cited line lands ~10px tall, which reads as "blurry, can't tell what it
  // points at". Give the document view room; keep the compact box for
  // quote-only citations so an empty dialog is not oversized.
  const hasPagePin = Boolean(
    citation.sourceId &&
    (citation.location?.pageNumber || citation.location?.cellAnchor),
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {children}
      <Dialog.Content
        data-testid="comment-knowledge-rule-citation-dialog"
        maxWidth={hasPagePin ? "min(920px, 92vw)" : undefined}
        className={
          hasPagePin
            ? "flex max-h-[85vh] flex-col gap-4 overflow-y-auto"
            : "flex max-h-[min(70vh,420px)] flex-col gap-4 overflow-y-auto"
        }
      >
        <Dialog.Title>
          {t("commentCitationLinks.knowledgeRuleSource")}
        </Dialog.Title>
        <Dialog.Description className="hidden">
          {t("commentCitationLinks.knowledgeRuleExcerpt")}
        </Dialog.Description>
        {statement ? (
          <Text size="2" weight="medium" data-testid="knowledge-rule-statement">
            {statement}
          </Text>
        ) : null}
        {sourceName ? (
          <Text
            size="2"
            className="text-puntt-neutral-gray-11"
            data-testid="knowledge-rule-citation-source-doc"
          >
            {sourceName}
          </Text>
        ) : null}
        {quote ? (
          <>
            <Text size="2" className="text-puntt-neutral-gray-11">
              <em>{t("commentCitationLinks.fromKnowledgeDatabase")}</em>
            </Text>
            <Preview text={quote} />
          </>
        ) : null}
        {hasPagePin && citation.sourceId ? (
          <KnowledgeRuleCitationPin
            sourceId={citation.sourceId}
            pageNumber={citation.location?.pageNumber}
            bbox={citation.location?.bbox}
            relX={citation.location?.rel_x}
            relY={citation.location?.rel_y}
            cellAnchor={citation.location?.cellAnchor}
          />
        ) : null}
        <Flex justify="end">
          <Button variant="outline" size="2" asChild>
            <Link
              data-testid="comment-knowledge-rule-citation-view-link"
              to={KNOWLEDGE_DATABASE_PATH}
              search={{
                ...KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
                tab: "rules",
                ruleId: citation.ruleId,
                ...(citation.sourceId ? { sourceId: citation.sourceId } : {}),
                // Opens the cited source WHERE the claim was read from -- the
                // workbook row or the pdf page -- rather than at the top of a
                // file the reader must then search. One definition, shared with
                // the panel that resolves it.
                ...(citationAnchorParam(citation)
                  ? { cite: citationAnchorParam(citation) }
                  : {}),
              }}
              target="_blank"
              rel="noreferrer"
              className="puntt-link flex gap-1"
            >
              <Text>
                {t("commentCitationLinks.viewRuleInKnowledgeDatabase")}
              </Text>
              <ArrowSquareOut width={16} height={16} />
            </Link>
          </Button>
        </Flex>
        <Dialog.Close>
          <Button type="button" variant="soft" color="gray">
            {t("commentCitationLinks.close")}
          </Button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function knowledgeRuleCitationLabel(
  citation: TicketMessageCitation,
  fallback: string,
): string {
  if (!isKnowledgeRuleCitation(citation)) return fallback;
  return citation.statement?.trim() || citation.quote?.trim() || fallback;
}
